import { useState, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import {
  Upload,
  FileSpreadsheet,
  Download,
  CheckCircle,
  XCircle,
  AlertTriangle,
  FileText,
  FileJson,
} from 'lucide-react';

interface ImportResult {
  imported: number;
  errors: string[];
}

interface JsonImportResult {
  categoriesCreated: number;
  categoriesExisting: number;
  toolTypesCreated: number;
  toolTypesSkipped: number;
  errors: string[];
}

interface ParsedRow {
  categoryName: string;
  name: string;
  partNumber?: string;
  manufacturer?: string;
  unitCost?: number;
  minQuantity?: number;
  initialQuantity?: number;
  toolroomName?: string;
  locationName?: string;
}

export function Import() {
  const [importMode, setImportMode] = useState<'csv' | 'json'>('json');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const jsonFileInputRef = useRef<HTMLInputElement>(null);
  const [parsedData, setParsedData] = useState<ParsedRow[]>([]);
  const [parseError, setParseError] = useState('');
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [jsonImportResult, setJsonImportResult] = useState<JsonImportResult | null>(null);
  const [jsonImporting, setJsonImporting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const importMutation = useMutation({
    mutationFn: async (data: ParsedRow[]) => {
      const res = await api.post<ImportResult>('/admin/import/tools', { data });
      if (!res.success) throw new Error(res.error || 'Failed to import');
      return res.data;
    },
    onSuccess: (result) => {
      setImportResult(result || { imported: 0, errors: [] });
      setParsedData([]);
    },
    onError: (err) => {
      setParseError(err instanceof Error ? err.message : 'Failed to import');
    },
  });

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setParseError('');
    setImportResult(null);

    try {
      const text = await file.text();
      const lines = text.split('\n').filter((line) => line.trim());

      if (lines.length < 2) {
        setParseError('File must have at least a header row and one data row');
        return;
      }

      const headers = parseCSVLine(lines[0]).map((h) => h.toLowerCase().trim());
      const requiredHeaders = ['category', 'name'];
      const missing = requiredHeaders.filter((h) => !headers.includes(h));

      if (missing.length > 0) {
        setParseError(`Missing required columns: ${missing.join(', ')}`);
        return;
      }

      const rows: ParsedRow[] = [];
      for (let i = 1; i < lines.length; i++) {
        const values = parseCSVLine(lines[i]);
        const row: Record<string, string> = {};

        headers.forEach((h, idx) => {
          row[h] = values[idx] || '';
        });

        if (!row.category || !row.name) continue;

        rows.push({
          categoryName: row.category,
          name: row.name,
          partNumber: row['part number'] || row.partnumber || undefined,
          manufacturer: row.manufacturer || undefined,
          unitCost: row['unit cost'] || row.unitcost || row.cost
            ? parseFloat(row['unit cost'] || row.unitcost || row.cost)
            : undefined,
          minQuantity: row['min quantity'] || row.minquantity || row.min
            ? parseInt(row['min quantity'] || row.minquantity || row.min)
            : undefined,
          initialQuantity: row['initial quantity'] || row.initialquantity || row.quantity
            ? parseInt(row['initial quantity'] || row.initialquantity || row.quantity)
            : undefined,
          toolroomName: row.toolroom || undefined,
          locationName: row.location || undefined,
        });
      }

      if (rows.length === 0) {
        setParseError('No valid data rows found');
        return;
      }

      setParsedData(rows);
    } catch {
      setParseError('Failed to parse file');
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }

    result.push(current.trim());
    return result;
  };

  const downloadTemplate = () => {
    const headers = [
      'Category',
      'Name',
      'Part Number',
      'Manufacturer',
      'Unit Cost',
      'Min Quantity',
      'Initial Quantity',
      'Toolroom',
      'Location',
    ];
    const example = [
      'End Mills',
      '1/4" 4-Flute Carbide',
      'EM-250-4F',
      'Harvey Tool',
      '45.00',
      '5',
      '10',
      'Main Tool Room',
      'Cabinet A-1',
    ];

    const csv = [headers.join(','), example.join(',')].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'tool-import-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleJsonFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setParseError('');
    setJsonImportResult(null);
    setJsonImporting(true);

    try {
      const text = await file.text();
      const json = JSON.parse(text);

      if (!json.toolTypes || !Array.isArray(json.toolTypes)) {
        throw new Error('Invalid file format: missing toolTypes array');
      }

      const response = await api.post<JsonImportResult>('/api/admin/import/library', json);

      if (response.success) {
        setJsonImportResult(response.data || null);
      } else {
        setParseError(response.error || 'Import failed');
      }
    } catch (err) {
      if (err instanceof SyntaxError) {
        setParseError('Invalid JSON file');
      } else {
        setParseError(err instanceof Error ? err.message : 'Import failed');
      }
    } finally {
      setJsonImporting(false);
      if (jsonFileInputRef.current) {
        jsonFileInputRef.current.value = '';
      }
    }
  };

  const handleExportLibrary = async () => {
    setExporting(true);
    setParseError('');

    try {
      const response = await api.get('/api/admin/export/library');

      if (response.success) {
        const blob = new Blob([JSON.stringify(response.data, null, 2)], {
          type: 'application/json',
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `tool-library-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        setParseError(response.error || 'Export failed');
      }
    } catch {
      setParseError('Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Import / Export</h1>
          <p className="text-foreground-muted">Import tool libraries or export your data</p>
        </div>
        <Button variant="ghost" onClick={handleExportLibrary} disabled={exporting}>
          <Download className="h-4 w-4" />
          {exporting ? 'Exporting...' : 'Export Library'}
        </Button>
      </div>

      {/* Mode Tabs */}
      <div className="flex gap-2 border-b border-border">
        <button
          className={`px-4 py-2 font-medium transition-colors ${
            importMode === 'json'
              ? 'text-primary border-b-2 border-primary'
              : 'text-foreground-muted hover:text-foreground'
          }`}
          onClick={() => {
            setImportMode('json');
            setParseError('');
            setImportResult(null);
            setJsonImportResult(null);
            setParsedData([]);
          }}
        >
          <FileJson className="h-4 w-4 inline mr-2" />
          JSON Library
        </button>
        <button
          className={`px-4 py-2 font-medium transition-colors ${
            importMode === 'csv'
              ? 'text-primary border-b-2 border-primary'
              : 'text-foreground-muted hover:text-foreground'
          }`}
          onClick={() => {
            setImportMode('csv');
            setParseError('');
            setImportResult(null);
            setJsonImportResult(null);
            setParsedData([]);
          }}
        >
          <FileSpreadsheet className="h-4 w-4 inline mr-2" />
          CSV Import
        </button>
      </div>

      {/* JSON Import Mode */}
      {importMode === 'json' && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileJson className="h-5 w-5" />
                JSON Tool Library Format
              </CardTitle>
            </CardHeader>
            <CardBody>
              <div className="space-y-4">
                <p className="text-sm text-foreground-muted">
                  Upload a JSON file containing tool categories and types. Existing tools with the
                  same name in the same category will be skipped.
                </p>

                <details className="text-sm">
                  <summary className="cursor-pointer text-foreground-muted hover:text-foreground font-medium">
                    View expected JSON format
                  </summary>
                  <pre className="mt-2 p-4 bg-surface-hover rounded-md overflow-x-auto text-xs">
{`{
  "categories": [
    {
      "name": "End Mills",
      "description": "Cutting tools for milling operations"
    }
  ],
  "toolTypes": [
    {
      "category": "End Mills",
      "name": "1/4\\" 4-Flute Carbide End Mill",
      "partNumber": "EM-250-4F",
      "manufacturer": "Kennametal",
      "description": "General purpose carbide end mill",
      "unitCost": 45.99,
      "minQuantity": 5,
      "expectedLifeHours": 50,
      "specifications": {
        "diameter": "0.25 in",
        "flutes": 4,
        "material": "Carbide"
      }
    }
  ]
}`}
                  </pre>
                </details>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <div
                className="border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-colors"
                onClick={() => jsonFileInputRef.current?.click()}
              >
                <input
                  type="file"
                  ref={jsonFileInputRef}
                  accept=".json"
                  onChange={handleJsonFileSelect}
                  className="hidden"
                />
                <FileJson className="h-12 w-12 mx-auto mb-4 text-foreground-muted" />
                <p className="text-lg font-medium mb-1">
                  {jsonImporting ? 'Importing...' : 'Click to upload JSON'}
                </p>
                <p className="text-sm text-foreground-muted">or drag and drop</p>
              </div>
            </CardBody>
          </Card>

          {jsonImportResult && (
            <Card>
              <CardBody>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    {jsonImportResult.errors.length === 0 ? (
                      <CheckCircle className="h-6 w-6 text-success" />
                    ) : (
                      <AlertTriangle className="h-6 w-6 text-warning" />
                    )}
                    <div>
                      <p className="font-medium">Import Complete</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-3 bg-surface-hover rounded-lg">
                      <p className="text-2xl font-bold">{jsonImportResult.toolTypesCreated}</p>
                      <p className="text-sm text-foreground-muted">Tools Created</p>
                    </div>
                    <div className="p-3 bg-surface-hover rounded-lg">
                      <p className="text-2xl font-bold">{jsonImportResult.toolTypesSkipped}</p>
                      <p className="text-sm text-foreground-muted">Tools Skipped</p>
                    </div>
                    <div className="p-3 bg-surface-hover rounded-lg">
                      <p className="text-2xl font-bold">{jsonImportResult.categoriesCreated}</p>
                      <p className="text-sm text-foreground-muted">Categories Created</p>
                    </div>
                    <div className="p-3 bg-surface-hover rounded-lg">
                      <p className="text-2xl font-bold">{jsonImportResult.categoriesExisting}</p>
                      <p className="text-sm text-foreground-muted">Categories Existed</p>
                    </div>
                  </div>

                  {jsonImportResult.errors.length > 0 && (
                    <div className="p-4 bg-error/10 rounded-lg">
                      <p className="font-medium text-error mb-2">Errors:</p>
                      <ul className="text-sm space-y-1">
                        {jsonImportResult.errors.map((err, idx) => (
                          <li key={idx} className="text-foreground-muted">{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          )}
        </>
      )}

      {/* CSV Import Mode */}
      {importMode === 'csv' && (
        <>
          <div className="flex justify-end">
            <Button variant="ghost" onClick={downloadTemplate}>
              <Download className="h-4 w-4" />
              Download CSV Template
            </Button>
          </div>

          {/* Instructions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                CSV Format
              </CardTitle>
            </CardHeader>
        <CardBody>
          <div className="space-y-4">
            <p className="text-sm text-foreground-muted">
              Upload a CSV file with the following columns. Required columns are marked with *.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium mb-2">Required Columns</h4>
                <ul className="text-sm space-y-1 text-foreground-muted">
                  <li><code className="bg-surface-hover px-1 rounded">Category</code> - Tool category name *</li>
                  <li><code className="bg-surface-hover px-1 rounded">Name</code> - Tool name *</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium mb-2">Optional Columns</h4>
                <ul className="text-sm space-y-1 text-foreground-muted">
                  <li><code className="bg-surface-hover px-1 rounded">Part Number</code></li>
                  <li><code className="bg-surface-hover px-1 rounded">Manufacturer</code></li>
                  <li><code className="bg-surface-hover px-1 rounded">Unit Cost</code></li>
                  <li><code className="bg-surface-hover px-1 rounded">Min Quantity</code></li>
                  <li><code className="bg-surface-hover px-1 rounded">Initial Quantity</code></li>
                  <li><code className="bg-surface-hover px-1 rounded">Toolroom</code></li>
                  <li><code className="bg-surface-hover px-1 rounded">Location</code></li>
                </ul>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Upload */}
      <Card>
        <CardBody>
          <div
            className="border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-colors"
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              type="file"
              ref={fileInputRef}
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
            />
            <FileSpreadsheet className="h-12 w-12 mx-auto mb-4 text-foreground-muted" />
            <p className="text-lg font-medium mb-1">Click to upload CSV</p>
            <p className="text-sm text-foreground-muted">or drag and drop</p>
          </div>
        </CardBody>
      </Card>

      {/* Import Result */}
      {importResult && (
        <Card>
          <CardBody>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                {importResult.errors.length === 0 ? (
                  <CheckCircle className="h-6 w-6 text-success" />
                ) : (
                  <AlertTriangle className="h-6 w-6 text-warning" />
                )}
                <div>
                  <p className="font-medium">
                    Imported {importResult.imported} tool(s)
                  </p>
                  {importResult.errors.length > 0 && (
                    <p className="text-sm text-foreground-muted">
                      {importResult.errors.length} error(s)
                    </p>
                  )}
                </div>
              </div>

              {importResult.errors.length > 0 && (
                <div className="p-4 bg-error/10 rounded-lg">
                  <p className="font-medium text-error mb-2">Errors:</p>
                  <ul className="text-sm space-y-1">
                    {importResult.errors.map((err, idx) => (
                      <li key={idx} className="text-foreground-muted">{err}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Preview */}
      {parsedData.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Preview ({parsedData.length} rows)</CardTitle>
            <Button
              onClick={() => importMutation.mutate(parsedData)}
              loading={importMutation.isPending}
            >
              <Upload className="h-4 w-4" />
              Import {parsedData.length} Tools
            </Button>
          </CardHeader>
          <CardBody>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Category</th>
                    <th>Name</th>
                    <th>Part Number</th>
                    <th>Manufacturer</th>
                    <th>Unit Cost</th>
                    <th>Min Qty</th>
                    <th>Initial Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedData.slice(0, 10).map((row, idx) => (
                    <tr key={idx}>
                      <td className="text-foreground-muted">{idx + 1}</td>
                      <td><Badge variant="neutral">{row.categoryName}</Badge></td>
                      <td className="font-medium">{row.name}</td>
                      <td className="text-foreground-muted">{row.partNumber || '-'}</td>
                      <td className="text-foreground-muted">{row.manufacturer || '-'}</td>
                      <td>{row.unitCost ? `$${row.unitCost.toFixed(2)}` : '-'}</td>
                      <td>{row.minQuantity || '-'}</td>
                      <td>{row.initialQuantity || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {parsedData.length > 10 && (
                <p className="text-center text-foreground-muted py-4">
                  ... and {parsedData.length - 10} more rows
                </p>
              )}
            </div>
          </CardBody>
        </Card>
      )}
        </>
      )}

      {/* Parse Error - shown for both modes */}
      {parseError && (
        <Card>
          <CardBody>
            <div className="flex items-center gap-3 text-error">
              <XCircle className="h-5 w-5 flex-shrink-0" />
              <p>{parseError}</p>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
